import React, { useState, useEffect } from "react";
import {
    Page,
    Card,
    DataTable,
    Checkbox,
    Button,
    Spinner,
    Banner,
    ProgressBar,
    Layout,
    Text,
    Pagination
} from "@shopify/polaris";
import { jsPDF } from "jspdf"; // Importar jsPDF
import "./PedidosNoPreparados.css"; // Estilos adicionales

function PedidosNoPreparados() {
    const [ordersList, setOrdersList] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [productsList, setProductsList] = useState([]);
    const [processing, setProcessing] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ORDERS_PER_PAGE = 100;

    // Imagen placeholder por si no existe otra
    const PLACEHOLDER_IMG = "https://dummyimage.com/100x100/cccccc/ffffff.png&text=No+image";

    useEffect(() => {
        fetchPendingOrders();
    }, []);

    // 1. Obtener pedidos pendientes
    const fetchPendingOrders = async () => {
        setLoading(true);
        setError(null);
        setProgress(0);

        try {
            console.log(process.env.REACT_APP_API_URL);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/orders/pending`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );
            if (!response.ok) throw new Error("Error al obtener los pedidos");

            const data = await response.json();
            console.log(data);

            const orders = data.orders.map((order) => {
                const lineItems = order.lineItems || [];
                // Debug log para ver estructura de packs
                lineItems.forEach(item => {
                    if (item.properties?.some(p => p.name.includes("__id"))) {
                        console.log(`[Order ${order.number}] Pack Item found:`, item);
                        console.log(`[Order ${order.number}] Properties:`, item.properties);
                    }
                });

                const hasPack = lineItems.some((item) =>
                    item.properties?.some((prop) => prop.name.includes("__id"))
                );
                const packCount = lineItems.filter((item) =>
                    item.properties?.some((prop) => prop.name.includes("__id"))
                ).length;

                return {
                    id: order.id,
                    number: order.number,
                    lineItemsCount: lineItems.length,
                    lineItems: lineItems,
                    hasPack: hasPack,
                    packCount: packCount
                };
            });

            setOrdersList(orders);
            setCurrentPage(1);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // 2. Maneja la selección de un pedido
    const handleSelectOrder = (orderId) => {
        setSelectedOrders((prev) =>
            prev.includes(orderId)
                ? prev.filter((id) => id !== orderId)
                : [...prev, orderId]
        );
    };

    // 3. Seleccionar/deseleccionar todos los pedidos
    const handleSelectAll = () => {
        const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
        const endIndex = startIndex + ORDERS_PER_PAGE;
        const pageOrders = ordersList.slice(startIndex, endIndex);
        const pageIds = pageOrders.map((order) => order.id);
        const allSelectedOnPage = pageIds.every((id) => selectedOrders.includes(id));
        if (allSelectedOnPage) {
            setSelectedOrders((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedOrders((prev) => Array.from(new Set([...prev, ...pageIds])));
        }
    };

    // 4. Procesar pedidos seleccionados
    const compileSelectedOrders = async () => {
        setProcessing(true);
        setLoading(true);
        setProductsList([]);
        setProgress(0);

        const totalItems = selectedOrders.length;
        let processedItems = 0;

        try {
            // Simulación de avance
            for (let i = 0; i < totalItems; i++) {
                processedItems += 1;
                setProgress(Math.round((processedItems / totalItems) * 100));
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            const updatedProductsList = [...productsList];

            // Recorremos solo los pedidos seleccionados
            for (const order of ordersList.filter((order) =>
                selectedOrders.includes(order.id)
            )) {
                for (const item of order.lineItems) {
                    const { name, quantity, properties, admin_graphql_api_id } = item;
                    const isPack =
                        properties && properties.some((prop) => prop.name.includes("__id"));

                    if (isPack) {
                        // 4A. Caso PACK: cada "prop" con __ lo consideramos SKU distinto
                        // Filtramos propiedades que no parecen ser SKUs (títulos, cantidades, etc)
                        const packItems = properties.filter((prop) => {
                            const name = prop.name.toLowerCase();
                            return name.includes("__id") && 
                                   !name.includes("title") && 
                                   !name.includes("name") && 
                                   !name.includes("qty") && 
                                   !name.includes("quantity") &&
                                   !name.includes("img") &&
                                   !name.includes("image") &&
                                   !name.includes("price");
                        });
                        
                        console.log(`[Processing Pack] Filtered properties:`, packItems);

                        for (const packItem of packItems) {
                            // 'packItem.value' es el SKU del producto en el pack
                            const packItemDetails = await parsePackItem(packItem.value);
                            const newSku = packItem.value || null; // El SKU del pack

                            // Buscamos si YA existe un producto en 'updatedProductsList' con ese SKU
                            const existingProduct = updatedProductsList.find(
                                (p) => p.sku && p.sku === newSku
                            );

                            if (existingProduct) {
                                // Sumamos la cantidad
                                existingProduct.quantity += 1;
                                // Si el existente tiene imagen placeholder y el "nuevo" no, actualizamos
                                if (
                                    isPlaceholder(existingProduct.image) &&
                                    !isPlaceholder(packItemDetails.image)
                                ) {
                                    existingProduct.image = packItemDetails.image;
                                }
                            } else {
                                // No existe => creamos un nuevo item
                                updatedProductsList.push({
                                    sku: newSku,
                                    name: packItemDetails.name,
                                    quantity: 1,
                                    image: packItemDetails.image || PLACEHOLDER_IMG
                                });
                            }
                        }
                    } else {
                        // 4B. Caso NO PACK
                        let productImage = PLACEHOLDER_IMG;
                        let productSku = null;

                        try {
                            const productVariantData = await fetchVariantImage(
                                admin_graphql_api_id
                            );
                            if (productVariantData) {
                                productImage =
                                    productVariantData.image || PLACEHOLDER_IMG;
                                productSku = productVariantData.sku || null;
                            }
                        } catch (err) {
                            console.error("Error al obtener la imagen del producto", err);
                        }

                        // Si no se obtuvo SKU, dejamos null o fallback
                        // Buscamos si YA existe un producto con el mismo SKU
                        // Si no hay SKU, podemos fallback a agrupar por name (depende de tu preferencia)
                        let existingProduct;
                        if (productSku) {
                            existingProduct = updatedProductsList.find(
                                (p) => p.sku && p.sku === productSku
                            );
                        } else {
                            // En caso de no tener SKU,
                            // OPCIONAL: agrupar por nombre, o no agrupar
                            existingProduct = updatedProductsList.find(
                                (p) =>
                                    !p.sku && // o p.sku === null
                                    p.name
                                        .toLowerCase()
                                        .normalize("NFD")
                                        .replace(/[\u0300-\u036f]/g, "")
                                        .replace(/\s+/g, "")
                                        .trim() ===
                                    name
                                        .toLowerCase()
                                        .normalize("NFD")
                                        .replace(/[\u0300-\u036f]/g, "")
                                        .replace(/\s+/g, "")
                                        .trim()
                            );
                        }

                        if (existingProduct) {
                            // Ya existe => sumamos la cantidad
                            existingProduct.quantity += quantity;
                            // Si la imagen actual es placeholder y la nueva no lo es => actualizar
                            if (
                                isPlaceholder(existingProduct.image) &&
                                !isPlaceholder(productImage)
                            ) {
                                existingProduct.image = productImage;
                            }
                        } else {
                            // No existe => creamos uno nuevo
                            updatedProductsList.push({
                                sku: productSku,
                                name,
                                quantity,
                                image: productImage
                            });
                        }
                    }
                }
            }

            setProductsList(updatedProductsList);
            console.log("Lista final:", updatedProductsList);
        } catch (err) {
            console.error(err);
            setError("Error al compilar los productos");
        } finally {
            setLoading(false);
            setProcessing(false);
        }
    };

    // 5. Generar el PDF
    const downloadPDF = async () => {
        const pdf = new jsPDF();
        pdf.setFont("helvetica", "normal");

        let yPosition = 20;
        const imageWidth = 40;

        pdf.setFontSize(8);
        pdf.text("Pedidos No Preparados", 14, yPosition);
        yPosition += 10;

        // Función auxiliar para cargar imágenes
        const loadImage = async (imageUrl) => {
            if (!imageUrl) {
                imageUrl = PLACEHOLDER_IMG;
            }
            try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const img = new Image();
                const objectURL = URL.createObjectURL(blob);

                return new Promise((resolve, reject) => {
                    img.onload = () => resolve(img);
                    img.onerror = reject;
                    img.src = objectURL;
                });
            } catch (error) {
                console.error("Error al cargar la imagen", error);
                return null;
            }
        };

        // Ordenar por nombre
        const productsToExport = [...productsList].sort((a, b) =>
            a.name.localeCompare(b.name)
        );

        for (const product of productsToExport) {
            const imageToLoad = product.image || PLACEHOLDER_IMG;
            const img = await loadImage(imageToLoad);

            if (img) {
                const imageHeight = (img.height * imageWidth) / img.width;
                pdf.addImage(img, "PNG", 14, yPosition, imageWidth / 2, imageHeight / 2);

                pdf.text(
                    `${product.name} - Cantidad: ${product.quantity} - SKU: ${product.sku}`,
                    imageWidth,
                    yPosition + 12
                );

                yPosition += imageHeight / 2 + 10;
                if (yPosition > pdf.internal.pageSize.height - 35) {
                    pdf.addPage();
                    yPosition = 20;
                }
            }
        }

        pdf.save("pedidos_no_preparados.pdf");
    };

    // Añadir esta nueva función para obtener el EAN
    const fetchVariantEan = async (variantId) => {
        try {
            // Extraer el ID numérico del GraphQL ID
            // El formato es "gid://shopify/ProductVariant/123456789"
            const idMatch = variantId.match(/ProductVariant\/(\d+)/);
            if (!idMatch) {
                console.error("ID de variante no válido:", variantId);
                return 'N/A';
            }
            const numericId = idMatch[1];

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/products/variant-ean`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ variantId: `gid://shopify/ProductVariant/${numericId}` })
                }
            );
            const data = await response.json();
            return data.barcode || 'N/A';
        } catch (err) {
            console.error("Error al obtener el EAN de la variante", err);
            return 'N/A';
        }
    };

    const downloadDetailedPDF = async () => {
        const pdf = new jsPDF();
        pdf.setFont("helvetica", "normal");
        let yPosition = 20;

        const selectedOrdersData = ordersList.filter(order => 
            selectedOrders.includes(order.id)
        );

        for (const order of selectedOrdersData) {
            pdf.setFontSize(12);
            pdf.text(`${order.number}`, 14, yPosition);
            yPosition += 10;

            pdf.setFontSize(6);
            for (const item of order.lineItems) {
                const isPack = item.properties?.some(prop => prop.name.includes("__id"));

                if (isPack) {
                    const packItems = item.properties.filter(prop => prop.name.includes("__id"));
                    for (const packItem of packItems) {
                        const packItemDetails = await parsePackItem(packItem.value);
                        // Repeat for each quantity
                        for (let i = 0; i < item.quantity; i++) {
                            const lineText = `[${packItemDetails.name} - "${packItemDetails.ean}"]`;
                            pdf.text(lineText, 20, yPosition);
                            yPosition += 4;

                            if (yPosition > pdf.internal.pageSize.height - 20) {
                                pdf.addPage();
                                yPosition = 20;
                            }
                        }
                    }
                } else {
                    const variantId = `gid://shopify/ProductVariant/${item.variant_id}`;
                    const ean = await fetchVariantEan(variantId);
                    
                    // Repeat for each quantity
                    for (let i = 0; i < item.quantity; i++) {
                        const lineText = `[${item.name} - "${ean}"]`;
                        pdf.text(lineText, 20, yPosition);
                        yPosition += 4;

                        if (yPosition > pdf.internal.pageSize.height - 20) {
                            pdf.addPage();
                            yPosition = 20;
                        }
                    }
                }

                if (yPosition > pdf.internal.pageSize.height - 20) {
                    pdf.addPage();
                    yPosition = 20;
                }
            }
            
            pdf.setDrawColor(200);
            pdf.line(14, yPosition, 196, yPosition);
            yPosition += 15;

            if (yPosition > pdf.internal.pageSize.height - 30) {
                pdf.addPage();
                yPosition = 20;
            }
        }

        pdf.save("pedidos_desglosados.pdf");
    };

    // 6. Función para parsear items de pack (obtiene nombre e imagen)
    const parsePackItem = async (sku) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/products/variant-sku`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ sku })
                }
            );
            const data = await response.json();
            
            // Obtener el EAN si tenemos el variantId
            let ean = 'N/A';
            console.log("Variant ID:", data);
            if (data.ean) {
                ean = data.ean;
            }

            return {
                name: data.name || "Variante desconocida",
                quantity: 1,
                image: data.image || PLACEHOLDER_IMG,
                variantId: data.variantId,
                sku: sku,
                ean: ean
            };
        } catch (err) {
            console.error("Error al procesar el item del pack:", err);
            return {
                name: "Variante desconocida",
                quantity: 1,
                image: PLACEHOLDER_IMG,
                variantId: null,
                sku: sku,
                ean: 'N/A'
            };
        }
    };

    // 7. Función para obtener la imagen/sku de una variante normal
    const fetchVariantImage = async (variantId) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/products/lineitem-variant-image`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ lineItemId: variantId })
                }
            );
            const data = await response.json();
            console.log(data);
            return {
                image: data?.imageUrl || null,
                sku: data?.sku || null
            };
        } catch (err) {
            console.error("Error al obtener la imagen de la variante", err);
            return null;
        }
    };

    // 8. Helper para comprobar si la imagen es el placeholder
    const isPlaceholder = (imgUrl) => {
        if (!imgUrl) return true;
        // Podemos buscar la substring 'dummyimage.com' o algo similar
        return imgUrl.includes("dummyimage.com");
    };

    // 9. Creación de filas para la DataTable
    const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
    const endIndex = startIndex + ORDERS_PER_PAGE;
    const paginatedOrders = ordersList.slice(startIndex, endIndex);
    const visibleIds = paginatedOrders.map((o) => o.id);
    const rows = paginatedOrders.map((order) => [
        <Checkbox
            checked={selectedOrders.includes(order.id)}
            onChange={() => handleSelectOrder(order.id)}
        />,
        order.number,
        `${order.lineItemsCount} ${order.hasPack ? `(${order.packCount} packs)` : ""}`
    ]);

    // 10. Manejo del botón para descargar PDF
    const handleButtonClick = async (event) => {
        event.preventDefault();
        try {
            await downloadPDF();
        } catch (error) {
            console.error("Error al generar el PDF", error);
        }
    };

    // Render
    return (
        <Page title="Pedidos No Preparados">
            <Layout>
                {loading && (
                    <Layout.Section>
                        <ProgressBar progress={progress} size="medium" color="primary" />
                    </Layout.Section>
                )}

                {error && (
                    <Layout.Section>
                        <Banner
                            title="Error"
                            status="critical"
                            onDismiss={() => setError(null)}
                        >
                            {error}
                        </Banner>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <Card>
                        <div className={processing ? "table-overlay" : ""}>
                            <DataTable
                                columnContentTypes={["text", "text", "text"]}
                                headings={[
                                    <Checkbox
                                        label="Seleccionar todos"
                                        checked={visibleIds.length > 0 && visibleIds.every((id) => selectedOrders.includes(id))}
                                        onChange={handleSelectAll}
                                    />,
                                    "Order ID",
                                    "Cantidad de Items"
                                ]}
                                rows={rows}
                                className="orders-table"
                            />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                                <Text variant="bodySm">Página {currentPage} de {Math.max(1, Math.ceil(ordersList.length / ORDERS_PER_PAGE))}</Text>
                                <Pagination
                                    hasPrevious={currentPage > 1}
                                    onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    hasNext={currentPage < Math.ceil(ordersList.length / ORDERS_PER_PAGE)}
                                    onNext={() => setCurrentPage((p) => Math.min(Math.ceil(ordersList.length / ORDERS_PER_PAGE), p + 1))}
                                />
                            </div>
                            {processing && (
                                <div className="overlay-content">
                                    <Text variant="bodyLg">Procesando... {progress}%</Text>
                                </div>
                            )}
                        </div>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Button
                        primary
                        onClick={compileSelectedOrders}
                        disabled={selectedOrders.length === 0 || processing}
                    >
                        {processing ? (
                            <>
                                <Spinner size="small" color="white" />
                                <Text variant="bodyMd" color="white">
                                    {progress}%
                                </Text>
                            </>
                        ) : (
                            "Procesar Pedidos Seleccionados"
                        )}
                    </Button>
                </Layout.Section>

                {productsList.length > 0 && !processing && (
                    <Layout.Section>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <Button onClick={handleButtonClick} primary>
                                Descargar Productos a Preparar
                            </Button>
                            <Button onClick={downloadDetailedPDF} primary>
                                Descargar Pedidos Desglosados
                            </Button>
                        </div>
                    </Layout.Section>
                )}

                {processing && (
                    <Layout.Section>
                        <Spinner accessibilityLabel="Procesando pedidos" size="large" />
                    </Layout.Section>
                )}
            </Layout>
        </Page>
    );
}

export default PedidosNoPreparados;
